import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { AdminUserDetailDTO, Role } from '@amrita/shared';
import { TopBar } from '@/components/ui/TopBar';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Link2, Copy, Check } from 'lucide-react';

interface InviteLinkResult {
  joinUrl: string;
  expiresAt: string;
}

/** Известные коды ошибок POST /api/admin/users/:id/invite → читаемый текст. */
function issueInviteErrorMessage(e: unknown): string {
  const m = (e as Error).message;
  if (m === 'user_inactive') return 'Пользователь заблокирован или на паузе — сначала снимите ограничение.';
  if (m === 'user_not_found') return 'Пользователь не найден.';
  return 'Не удалось выдать ссылку.';
}

export default function AdminUserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const qc = useQueryClient();

  const [inviteLinkResult, setInviteLinkResult] = useState<InviteLinkResult | null>(null);
  const [copied, setCopied] = useState(false);

  const issueInvite = useMutation({
    mutationFn: () =>
      api.post<{ token: string; joinUrl: string; expiresAt: string }>(
        `/api/admin/users/${id}/invite`,
        {},
      ),
    onSuccess: (res) => setInviteLinkResult({ joinUrl: res.joinUrl, expiresAt: res.expiresAt }),
  });

  async function copyLink(url: string) {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      window.prompt('Скопируйте ссылку вручную:', url);
    }
  }

  const { data: u, isLoading } = useQuery<AdminUserDetailDTO>({
    queryKey: ['admin-user', id],
    queryFn: () => api.get<AdminUserDetailDTO>(`/api/admin/users/${id}`),
    enabled: !!id,
  });

  const setRole = useMutation({
    mutationFn: (role: Role) => api.put<{ ok: true }>(`/api/admin/users/${id}/role`, { role }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-user', id] });
      qc.invalidateQueries({ queryKey: ['admin-users'] });
    },
  });

  const pauseUser = useMutation({
    mutationFn: () => api.post<{ ok: true }>(`/api/admin/users/${id}/pause`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-user', id] });
      qc.invalidateQueries({ queryKey: ['admin-users'] });
    },
  });

  const deleteUser = useMutation({
    mutationFn: () => api.delete<{ ok: true }>(`/api/admin/users/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-users'] });
      nav('/admin/users');
    },
  });

  if (isLoading || !u) return <div className="p-6 text-tg-hint">Загрузка…</div>;

  const confirm = (msg: string, fn: () => void) => {
    if (window.confirm(msg)) fn();
  };

  return (
    <div className="safe-bottom min-h-full">
      <TopBar title={`@${u.tgUsername ?? 'user'}`} subtitle={u.role} />

      <div className="p-4 space-y-3">
        <Card>
          <div className="text-sm text-tg-hint">Telegram ID</div>
          <div className="font-mono text-sm">{u.tgId}</div>
          <div className="text-sm text-tg-hint mt-2">Добавлен</div>
          <div>{new Date(u.createdAt).toLocaleString('ru-RU')}</div>
          <div className="text-sm text-tg-hint mt-2">Последний визит</div>
          <div>{u.lastSeenAt ? new Date(u.lastSeenAt).toLocaleString('ru-RU') : 'никогда'}</div>
        </Card>

        {u.curriculumProgress && (
          <Card>
            <div className="font-semibold mb-2">Прогресс курса</div>
            <div className="text-sm">
              Урок {u.curriculumProgress.day} из {u.curriculumProgress.total} (
              {u.curriculumProgress.percent}%)
            </div>
          </Card>
        )}

        <Card>
          <div className="font-semibold mb-2">Статистика</div>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div>
              <span className="text-tg-hint">Квизы:</span> {u.stats.quizzesPassed}/{u.stats.quizzesTotal}
            </div>
            <div>
              <span className="text-tg-hint">Средний балл:</span>{' '}
              {u.stats.quizAvgScore?.toFixed(1) ?? '—'}
            </div>
            <div>
              <span className="text-tg-hint">Поиск (7д):</span> {u.stats.searchQueries7d}
            </div>
            <div>
              <span className="text-tg-hint">Закладок:</span> {u.stats.bookmarks}
            </div>
          </div>
        </Card>

        <Card>
          <div className="font-semibold mb-2">Последние действия</div>
          <div className="space-y-1 text-sm max-h-80 overflow-y-auto">
            {u.recentActions.length === 0 && <div className="text-tg-hint">Действий пока нет</div>}
            {u.recentActions.slice(0, 30).map((a) => (
              <div key={a.id} className="flex gap-2">
                <span className="text-tg-hint whitespace-nowrap text-xs">
                  {new Date(a.createdAt).toLocaleTimeString('ru-RU', {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </span>
                <span>{a.action}</span>
              </div>
            ))}
          </div>
        </Card>

        <Card>
          <div className="font-semibold mb-2">Изменить роль</div>
          <div className="grid grid-cols-2 gap-2">
            {(['admin', 'consultant', 'intern', 'guest'] as Role[]).map((r) => (
              <Button
                key={r}
                variant={u.role === r ? 'primary' : 'secondary'}
                disabled={setRole.isPending || u.role === r}
                onClick={() => confirm(`Сделать роль: ${r}?`, () => setRole.mutate(r))}
              >
                {r}
              </Button>
            ))}
          </div>
        </Card>

        <Card className="space-y-2">
          <div className="font-semibold mb-2">Ссылка для входа</div>
          <div className="text-xs text-tg-hint">
            Выдаёт новую ссылку-приглашение — работает и как сброс пароля.
          </div>
          {issueInvite.isError && (
            <div className="text-xs text-red-500">{issueInviteErrorMessage(issueInvite.error)}</div>
          )}
          {inviteLinkResult ? (
            <>
              <div className="break-all text-sm bg-tg-bg rounded-xl p-3 font-mono">
                {inviteLinkResult.joinUrl}
              </div>
              <div className="text-xs text-tg-hint">
                Действительна до {new Date(inviteLinkResult.expiresAt).toLocaleDateString('ru-RU')}
              </div>
              <Button
                variant="secondary"
                className="w-full"
                onClick={() => copyLink(inviteLinkResult.joinUrl)}
              >
                {copied ? (
                  <>
                    <Check className="w-4 h-4 mr-1" /> Скопировано
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4 mr-1" /> Скопировать
                  </>
                )}
              </Button>
            </>
          ) : (
            <Button
              variant="secondary"
              className="w-full"
              disabled={issueInvite.isPending}
              onClick={() => issueInvite.mutate()}
            >
              <Link2 className="w-4 h-4 mr-1" />
              {issueInvite.isPending ? 'Генерируем…' : 'Ссылка для входа'}
            </Button>
          )}
        </Card>

        <div className="grid grid-cols-2 gap-2">
          <Button
            variant="secondary"
            disabled={pauseUser.isPending}
            onClick={() => confirm('Поставить на паузу?', () => pauseUser.mutate())}
          >
            ⏸ Пауза
          </Button>
          <Button
            variant="secondary"
            className="bg-red-500/10 text-red-500"
            disabled={deleteUser.isPending}
            onClick={() => confirm('УДАЛИТЬ пользователя? (soft delete)', () => deleteUser.mutate())}
          >
            🗑 Удалить
          </Button>
        </div>
      </div>
    </div>
  );
}
